import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { getLedgerAccountById } from '../../../services/loanService';
import type { LedgerAccount } from '../../../services/schemas/loanServiceSchemas/ledgerAccount';
import type { LedgerInstallment } from '../../../services/schemas/loanServiceSchemas/ledgerInstallment';
import LoanApplicationComments from '../../../components/common/LoanApplicationComments';
import type { LoanApplicationStatus } from '../../../services/schemas/loanServiceSchemas/loanApplicationStatus';

const LoanBookRecordView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loanAccount, setLoanAccount] = useState<LedgerAccount | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      toast.error('Invalid loan record ID');
      navigate('/loan-book/invoices');
      return;
    }

    const fetchRecord = async () => {
      try {
        setIsLoading(true);
        const account = await getLedgerAccountById(id);
        setLoanAccount(account);
      } catch (error) {
        console.error('Error fetching loan book record:', error);
        toast.error('Failed to fetch loan record details');
        navigate('/loan-book/invoices');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecord();
  }, [id, navigate]);

  if (isLoading) {
    return (
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading loan record details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!loanAccount) {
    return (
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          <div className="text-center py-5">
            <i className="fa fa-file-o fa-3x text-muted mb-3"></i>
            <h5>Loan record not found</h5>
            <p className="text-muted">The requested loan record could not be found.</p>
            <button className="btn btn-primary mt-3" onClick={() => navigate('/loan-book/invoices')}>
              <i className="fa fa-arrow-left me-2"></i>
              Back to Loan Book
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to get product code based on installments
  const getProductCode = (account: LedgerAccount): string => {
    if (!account.installments || account.installments.length === 0) {
      return account.productCode || 'N/A';
    }
    return account.installments.length === 1 ? 'Once off loan' : 'Monthly Loan';
  };

  // Helper function to get next installment due date
  const getNextInstallmentDueDate = (account: LedgerAccount): string => {
    if (!account.installments || account.installments.length === 0) {
      return account.firstDueDate ? formatDate(account.firstDueDate) : 'N/A';
    }

    // Find the next unpaid installment (PENDING, DUE, or OVERDUE)
    const unpaidInstallment = account.installments.find(
      inst => inst.status === 'PENDING' || inst.status === 'DUE' || inst.status === 'OVERDUE'
    );
    if (unpaidInstallment?.dueDate) {
      return formatDate(unpaidInstallment.dueDate);
    }

    // If no unpaid installment found, get the earliest future installment
    const futureInstallments = account.installments
      .filter(inst => inst.dueDate && new Date(inst.dueDate) >= new Date())
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
    if (futureInstallments.length > 0) {
      return formatDate(futureInstallments[0].dueDate);
    }

    // Fallback to the last installment's due date
    const sortedInstallments = [...account.installments]
      .sort((a, b) => new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime());
    if (sortedInstallments.length > 0 && sortedInstallments[0].dueDate) {
      return formatDate(sortedInstallments[0].dueDate);
    }

    // Final fallback to firstDueDate
    return account.firstDueDate ? formatDate(account.firstDueDate) : 'N/A';
  };

  const getAccountStatusStyles = (status?: string) => {
    const normalizedStatus = (status || '').toUpperCase();
    switch (normalizedStatus) {
      case 'ACTIVE':
        return { badge: 'success', circle: 'bg-success', icon: 'check-circle', iconColor: 'text-success' };
      case 'IN_ARREARS':
        return { badge: 'warning', circle: 'bg-warning', icon: 'exclamation-triangle', iconColor: 'text-warning' };
      case 'DEFAULTED':
        return { badge: 'danger', circle: 'bg-danger', icon: 'exclamation-triangle', iconColor: 'text-danger' };
      case 'WRITTEN_OFF':
        return { badge: 'dark', circle: 'bg-dark', icon: 'ban', iconColor: 'text-dark' };
      case 'CLOSED':
        return { badge: 'secondary', circle: 'bg-secondary', icon: 'lock', iconColor: 'text-secondary' };
      default:
        return { badge: 'secondary', circle: 'bg-secondary', icon: 'circle', iconColor: 'text-secondary' };
    }
  };

  const accountStatusStyles = getAccountStatusStyles(loanAccount.status);

  const getLoanApplicationStage = (status?: string): LoanApplicationStatus => {
    switch (status) {
      case 'ACTIVE':
        return 'ACTIVE';
      case 'DEFAULT':
      case 'PAST_DUE':
      case 'WRITTEN_OFF':
      case 'RESTRUCTURED':
        return 'ACTIVE';
      case 'CLOSED':
      case 'PAID':
        return 'COMPLETED';
      case 'CANCELLED':
        return 'CANCELLED';
      default:
        return 'ACTIVE';
    }
  };

  return (
    <>
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <div className="row g-3 align-items-center">
            <div className="col">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb bg-transparent mb-2">
                  <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                  <li className="breadcrumb-item"><Link className="text-secondary" to="/loan-book/invoices">Loan Book</Link></li>
                  <li className="breadcrumb-item active" aria-current="page">Record Details</li>
                </ol>
              </nav>
              <h2 className="mb-1 fw-bold">{loanAccount.customerName || 'Loan Record'}</h2>
              <p className="text-muted mb-0">
                <i className="fa fa-file-text me-2"></i>
                {loanAccount.accountNumber || loanAccount.accountId.substring(0, 8)}
              </p>
            </div>
            <div className="col-auto">
              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-light"
                  onClick={() => navigate(-1)}
                >
                  <i className="fa fa-arrow-left me-2"></i>
                  Back
                </button>
                {loanAccount && (
                  <>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => navigate('/loan-book/invoices')}
                    >
                      <i className="fa fa-book me-2"></i>
                      Back to Loan Book
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate(`/loan-accounts/${loanAccount.accountId}`)}
                    >
                      <i className="fa fa-eye me-2"></i>
                      View Account Details
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          {/* Summary Cards */}
          <div className="row g-4 mb-4">
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted small mb-1">Disbursed Amount</p>
                      <h3 className="mb-0 fw-bold text-primary">{formatCurrency(loanAccount.disbursedAmount || 0)}</h3>
                    </div>
                    <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                      <i className="fa fa-arrow-up fa-2x text-primary" style={{ display: 'block' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted small mb-1">Total Outstanding</p>
                      <h3 className="mb-0 fw-bold text-danger">{formatCurrency(loanAccount.totalOutstanding || 0)}</h3>
                    </div>
                    <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                      <i className="fa fa-money fa-2x text-danger" style={{ display: 'block' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted small mb-1">Account Status</p>
                      <div className="mt-1">
                        <span className={`badge bg-${accountStatusStyles.badge} px-3 py-2`}>
                          {loanAccount.status}
                        </span>
                      </div>
                    </div>
                    <div className={`${accountStatusStyles.circle} bg-opacity-10 rounded-circle p-3`}>
                      <i className={`fa fa-${accountStatusStyles.icon} fa-2x ${accountStatusStyles.iconColor}`} style={{ display: 'block' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted small mb-1">Product Code</p>
                      <h5 className="mb-0 fw-bold text-info">{getProductCode(loanAccount)}</h5>
                    </div>
                    <div className="bg-info bg-opacity-10 rounded-circle p-3">
                      <i className="fa fa-cube fa-2x text-info" style={{ display: 'block' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content - Side by Side Layout */}
          <div className="row g-4 mb-4">
            {/* Loan Details Section - More Compact */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0 py-3">
                  <h5 className="mb-0 fw-bold d-flex align-items-center">
                    <i className="fa fa-info-circle text-primary me-2"></i>
                    Loan Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {/* Account Number */}
                    <div className="col-12">
                      <div className="d-flex align-items-start p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="flex-shrink-0 me-2">
                          <div className="rounded-circle p-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                            <i className="fa fa-barcode text-primary"></i>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <small className="text-muted d-block mb-1">Account Number</small>
                          <div className="fw-semibold">{loanAccount.accountNumber || loanAccount.accountId.substring(0, 8)}</div>
                        </div>
                      </div>
                    </div>
                    {/* Product Code */}
                    <div className="col-12">
                      <div className="d-flex align-items-start p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="flex-shrink-0 me-2">
                          <div className="bg-success bg-opacity-10 rounded-circle p-2">
                            <i className="fa fa-cube text-success"></i>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <small className="text-muted d-block mb-1">Product Code</small>
                          <div className="fw-semibold">{getProductCode(loanAccount)}</div>
                        </div>
                      </div>
                    </div>
                    {/* Loan Amount */}
                    <div className="col-12">
                      <div className="d-flex align-items-start p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="flex-shrink-0 me-2">
                          <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                            <i className="fa fa-money text-primary"></i>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <small className="text-muted d-block mb-1">Loan Amount</small>
                          <div className="fw-semibold">{formatCurrency(loanAccount.disbursedAmount || 0)}</div>
                        </div>
                      </div>
                    </div>
                    {/* Commencement Date */}
                    <div className="col-12">
                      <div className="d-flex align-items-start p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="flex-shrink-0 me-2">
                          <div className="bg-info bg-opacity-10 rounded-circle p-2">
                            <i className="fa fa-calendar text-info"></i>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <small className="text-muted d-block mb-1">Commencement Date</small>
                          <div className="fw-semibold">{formatDate(loanAccount.disbursementDate || '')}</div>
                        </div>
                      </div>
                    </div>
                    {/* Next Installment */}
                    <div className="col-12">
                      <div className="d-flex align-items-start p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="flex-shrink-0 me-2">
                          <div className="rounded-circle p-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(255,193,7,0.2)' }}>
                            <i className="fa fa-calendar-check-o text-warning"></i>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <small className="text-muted d-block mb-1">Next Installment</small>
                          <div className="fw-semibold">{getNextInstallmentDueDate(loanAccount)}</div>
                        </div>
                      </div>
                    </div>
                    {/* Maturity Date */}
                    <div className="col-12">
                      <div className="d-flex align-items-start p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="flex-shrink-0 me-2">
                          <div className="bg-danger bg-opacity-10 rounded-circle p-2">
                            <i className="fa fa-calendar-check-o text-danger"></i>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <small className="text-muted d-block mb-1">Maturity Date</small>
                          <div className="fw-semibold">{loanAccount.maturityDate ? formatDate(loanAccount.maturityDate) : 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Details */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0 py-3">
                  <h5 className="mb-0 fw-bold d-flex align-items-center">
                    <i className="fa fa-user text-primary me-2"></i>
                    Customer Details
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="mb-3">
                    <div className="d-flex align-items-start p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="flex-shrink-0 me-3">
                        <div className="rounded-circle p-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                          <i className="fa fa-user text-primary"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <small className="text-muted d-block mb-1">Customer Name</small>
                        <div className="fw-semibold">{loanAccount.customerName || 'N/A'}</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="d-flex align-items-start p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="flex-shrink-0 me-3">
                        <div className="bg-info bg-opacity-10 rounded-circle p-2">
                          <i className="fa fa-id-card text-info"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <small className="text-muted d-block mb-1">Customer Number</small>
                        <div className="fw-semibold">{loanAccount.customerNumber || 'N/A'}</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-top">
                    <Link
                      to={`/customers/${loanAccount.customerNumber}/accounts`}
                      className="btn btn-outline-primary w-100"
                    >
                      <i className="fa fa-list me-2"></i>
                      View All Customer Accounts
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Schedule / Installments - At the bottom */}
          {loanAccount.installments && loanAccount.installments.length > 0 && (
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-0 py-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 fw-bold d-flex align-items-center">
                        <i className="fa fa-calendar-check-o text-success me-2"></i>
                        Payment Schedule ({loanAccount.installments.length} Installments)
                      </h5>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    <DataTable
                      columns={[
                        {
                          name: '#',
                          selector: (row: LedgerInstallment) => row.sequenceNum || 0,
                          sortable: true,
                          cell: (row: LedgerInstallment) => (
                            <div className="fw-bold text-primary">{row.sequenceNum || 0}</div>
                          ),
                          minWidth: '60px',
                        },
                        {
                          name: 'Due Date',
                          selector: (row: LedgerInstallment) => row.dueDate || '',
                          sortable: true,
                          cell: (row: LedgerInstallment) => (
                            <div>{formatDate(row.dueDate || '')}</div>
                          ),
                          minWidth: '130px',
                        },
                        {
                          name: 'Total Due',
                          selector: (row: LedgerInstallment) => (row.principalDue || 0) + (row.interestDue || 0) + (row.feesDue || 0),
                          sortable: true,
                          cell: (row: LedgerInstallment) => {
                            const totalDue = (row.principalDue || 0) + (row.interestDue || 0) + (row.feesDue || 0);
                            return (
                              <div className="text-end fw-bold text-primary">{formatCurrency(totalDue)}</div>
                            );
                          },
                          minWidth: '130px',
                        },
                        {
                          name: 'Total Paid',
                          selector: (row: LedgerInstallment) => (row.principalPaid || 0) + (row.interestPaid || 0) + (row.feesPaid || 0),
                          sortable: true,
                          cell: (row: LedgerInstallment) => {
                            const totalPaid = (row.principalPaid || 0) + (row.interestPaid || 0) + (row.feesPaid || 0);
                            return (
                              <div className="text-end fw-bold text-success">{formatCurrency(totalPaid)}</div>
                            );
                          },
                          minWidth: '130px',
                        },
                        {
                          name: 'Remaining',
                          selector: (row: LedgerInstallment) => {
                            const totalDue = (row.principalDue || 0) + (row.interestDue || 0) + (row.feesDue || 0);
                            const totalPaid = (row.principalPaid || 0) + (row.interestPaid || 0) + (row.feesPaid || 0);
                            return totalDue - totalPaid;
                          },
                          sortable: true,
                          cell: (row: LedgerInstallment) => {
                            const totalDue = (row.principalDue || 0) + (row.interestDue || 0) + (row.feesDue || 0);
                            const totalPaid = (row.principalPaid || 0) + (row.interestPaid || 0) + (row.feesPaid || 0);
                            const remaining = totalDue - totalPaid;
                            return (
                              <div className={`text-end fw-bold ${remaining > 0 ? 'text-danger' : 'text-success'}`}>
                                {formatCurrency(remaining)}
                              </div>
                            );
                          },
                          minWidth: '130px',
                        },
                        {
                          name: 'Status',
                          selector: (row: LedgerInstallment) => row.status || 'PENDING',
                          sortable: true,
                          cell: (row: LedgerInstallment) => {
                            const status = row.status || 'PENDING';
                            const statusColors: Record<string, string> = {
                              PAID: 'success',
                              PARTIALLY_PAID: 'warning',
                              OVERDUE: 'danger',
                              DUE: 'info',
                              PENDING: 'secondary',
                              WAIVED: 'light',
                            };
                            return (
                              <span className={`badge bg-${statusColors[status] || 'secondary'} px-3 py-2`}>
                                {status}
                              </span>
                            );
                          },
                          minWidth: '130px',
                        },
                      ]}
                      data={loanAccount.installments}
                      highlightOnHover
                      striped
                      noDataComponent={
                        <div className="text-center py-4">
                          <i className="fa fa-calendar fa-2x text-muted mb-2"></i>
                          <p className="text-muted">No installments found</p>
                        </div>
                      }
                      customStyles={{
                        headRow: {
                          style: {
                            backgroundColor: '#f8f9fa',
                            borderBottom: '2px solid #dee2e6',
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                          },
                        },
                        headCells: {
                          style: {
                            paddingLeft: '1rem',
                            paddingRight: '1rem',
                            paddingTop: '1rem',
                            paddingBottom: '1rem',
                            whiteSpace: 'nowrap',
                            fontSize: '0.8rem',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '1rem',
                            paddingRight: '1rem',
                            paddingTop: '0.75rem',
                            paddingBottom: '0.75rem',
                            fontSize: '0.85rem',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            borderBottom: '1px solid #f8f9fa',
                          },
                          stripedStyle: {
                            backgroundColor: '#ffffff',
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default LoanBookRecordView;

